<div class="tw:p-4" data-test="edit-dialog">
  <h2 class="tw:text-lg tw:font-bold tw:mb-1">{{data.item.displayName}}</h2>
  <div class="tw:text-xs tw:opacity-50 tw:mb-4">{{data.item.switchKey}}</div>

  @if (errors.length > 0) {
    <div class="tw:mb-4 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" data-test="edit-errors">
      @for (e of errors; track e) {
        <div>{{e}}</div>
      }
    </div>
  }

  <section class="tw:mb-4">
    <label class="field__toggle">
      <input type="checkbox" role="switch" name="isEnabled" [(ngModel)]="isEnabled"
        [attr.aria-checked]="isEnabled" [attr.aria-label]="data.item.displayName" data-test="edit-enabled" />
      <span class="field__toggle-track"></span>
      <span class="tw:text-sm tw:opacity-75">The master switch. Off turns the feature off for everyone, whatever else is set.</span>
    </label>
  </section>

  @if (adopted) {
    <div class="tw:mb-4 tw:p-3 tw:border tw:border-green-600 tw:bg-green-50 tw:text-green-900 tw:rounded" data-test="edit-adopted-note">
      Adopted: access is managed on Role Features
    </div>
  }

  <!-- An adopted switch ignores its targeting, so every field below is read-only; the master switch above stays
       usable, because turning it off is the pull-back. -->
  <fieldset class="tw:border-0 tw:p-0 tw:m-0 tw:min-w-0" [disabled]="adopted" data-test="edit-targeting">
  <section class="tw:mb-4">
    <label class="tw:block tw:font-bold" for="rollout">Rollout</label>
    <div class="tw:text-sm tw:opacity-75">The share of eligible users who get it. Each user always gets the same answer.</div>
    <div class="tw:flex tw:items-center tw:gap-2">
      <input type="range" min="0" max="100" step="1" name="rolloutRange" [(ngModel)]="rolloutPercent" class="tw:flex-1" />
      <input id="rollout" type="number" min="0" max="100" step="1" name="rolloutPercent" [(ngModel)]="rolloutPercent"
        class="tw:w-20" data-test="edit-rollout" /> %
    </div>
  </section>

  <section class="tw:mb-4">
    <div class="tw:font-bold">Roles</div>
    <div class="tw:text-sm tw:opacity-75">None ticked means every role.</div>
    @if (narrowed) {
      <div class="tw:text-sm tw:opacity-75" data-test="edit-roles-hint">Only roles that hold {{data.item.featureName}} can be targeted</div>
    }
    <div class="tw:flex tw:flex-wrap tw:gap-x-4">
      @for (role of roles; track role.code) {
        <label class="field__checkbox" data-test="edit-role">
          <input type="checkbox" [checked]="selectedRoles.has(role.code.toUpperCase())" (change)="toggleRole(role.code)" />
          {{role.code}}<span class="tw:text-sm tw:opacity-60"> {{role.description}}</span>
          @if (notGranted(role.code)) {
            <span class="tw:text-sm tw:text-red-700" data-test="edit-role-not-granted"> does not hold {{data.item.featureName}}</span>
          }
        </label>
      }
    </div>
  </section>

  <section class="tw:mb-4">
    <div class="tw:font-bold">Brands</div>
    <div class="tw:text-sm tw:opacity-75">None ticked means every brand.</div>
    <div class="tw:flex tw:flex-wrap tw:gap-x-4">
      @for (brand of brands; track brand.companyId) {
        <label class="field__checkbox" data-test="edit-brand">
          <input type="checkbox" [checked]="selectedBrands.has(brand.companyId)" (change)="toggleBrand(brand.companyId)" />
          {{brand.brandShortCode || brand.name}}
        </label>
      }
    </div>
  </section>

  <section class="tw:mb-4">
    <div class="tw:font-bold">Named users</div>
    <div class="tw:text-sm tw:opacity-75">Included users get it whatever the roles, brands and rollout say. Excluded users never do.</div>
    <div class="tw:flex tw:gap-2 tw:my-2">
      <app-typeahead class="tw:flex-1" inputClass="tw:w-full" name="searchTerm" [(ngModel)]="searchTerm"
        (keydown.enter)="$event.preventDefault(); search()" placeholder="Search by name or email" inputTestId="edit-user-search"
        [enabled]="!!data.userAutocomplete" [source]="searchUserSuggestions" [displayWith]="suggestionUserName"
        [secondaryWith]="suggestionDetail" [valueWith]="suggestionSearchText" (picked)="search()"></app-typeahead>
      <button type="button" class="btn" (click)="search()" [disabled]="searching">Search</button>
    </div>
    @if (searchError) {
      <div class="tw:text-sm tw:text-red-700">{{searchError}}</div>
    }
    @for (u of searchResults; track u.id) {
      <div class="tw:flex tw:items-center tw:gap-2 tw:text-sm" data-test="edit-user-result">
        <span class="tw:flex-1">{{userName(u)}}</span>
        <button type="button" class="btn" (click)="include(u)" data-test="edit-include">Include</button>
        <button type="button" class="btn" (click)="exclude(u)" data-test="edit-exclude">Exclude</button>
      </div>
    }
    <div class="tw:mt-2 tw:text-sm">
      <div><strong>Included:</strong>
        @for (u of includeUsers; track u.id) {
          <span class="tw:inline-block tw:mr-2" data-test="edit-included-user">{{u.name}}
            <button type="button" class="tw:opacity-60" (click)="removeInclude(u.id)" aria-label="Remove">×</button></span>
        } @empty { <span class="tw:opacity-50">nobody</span> }
      </div>
      <div><strong>Excluded:</strong>
        @for (u of excludeUsers; track u.id) {
          <span class="tw:inline-block tw:mr-2" data-test="edit-excluded-user">{{u.name}}
            <button type="button" class="tw:opacity-60" (click)="removeExclude(u.id)" aria-label="Remove">×</button></span>
        } @empty { <span class="tw:opacity-50">nobody</span> }
      </div>
    </div>
  </section>

  <section class="tw:mb-4">
    <div class="tw:font-bold">Schedule</div>
    <div class="tw:text-sm tw:opacity-75">Your local time. Leave either blank for no limit. The end is when it switches off.</div>
    <div class="tw:flex tw:flex-wrap tw:gap-4">
      <label>Start <input type="datetime-local" name="startLocal" [(ngModel)]="startLocal" data-test="edit-start" /></label>
      <label>End <input type="datetime-local" name="endLocal" [(ngModel)]="endLocal" data-test="edit-end" /></label>
    </div>
  </section>
  </fieldset>

  <div class="tw:flex tw:justify-end tw:gap-2">
    <button type="button" class="btn" (click)="cancel()" data-test="edit-cancel">Cancel</button>
    <button type="button" class="btn btn-primary" (click)="save()" data-test="edit-save">Save</button>
  </div>
</div>
